import {
  ELEMENTS_PROTOCOL_TEXT_LIMIT,
  hasExactKeys,
  isBoundedProtocolString,
  isRecord,
} from './protocol-validation';

/**
 * Window-message protocol for the detached Elements action frame.
 *
 * The SDK accepts these messages only after action-shell.ts has verified the
 * sending Window and Hosted Checkout origin. Keeping schema validation here
 * makes the remaining trust decision explicit and independently testable.
 */
export const ELEMENTS_ACTION_CHANNEL = 'paypercut.elements.action' as const;

export const ElementsActionMessageType = {
  FrameReady: 'PAYPERCUT_ELEMENTS_ACTION_FRAME_READY',
  Init: 'PAYPERCUT_ELEMENTS_ACTION_INIT',
  Cancel: 'PAYPERCUT_ELEMENTS_ACTION_CANCEL',
  Wake: 'PAYPERCUT_ELEMENTS_ACTION_WAKE',
  Phase: 'PAYPERCUT_ELEMENTS_ACTION_PHASE',
  Result: 'PAYPERCUT_ELEMENTS_ACTION_RESULT',
  Error: 'PAYPERCUT_ELEMENTS_ACTION_ERROR',
} as const;

export type ElementsActionWakeSource = 'visibility' | 'pageshow' | 'online' | 'focus';

export type ElementsActionPhase = 'loading' | 'content_ready' | 'completing';

export type ElementsActionInitMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Init;
  requestId: string;
  payload: {
    source: 'component' | 'payment_confirmation';
    actionId: string;
    actionType: 'three_d_secure';
  };
};

export type ElementsActionCancelMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Cancel;
  requestId: string;
  payload: Record<string, never>;
};

export type ElementsActionWakeMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Wake;
  requestId: string;
  payload: { source: ElementsActionWakeSource };
};

export const createElementsActionWakeMessage = (
  requestId: string,
  source: ElementsActionWakeSource,
): ElementsActionWakeMessage => ({
  channel: ELEMENTS_ACTION_CHANNEL,
  type: ElementsActionMessageType.Wake,
  requestId,
  payload: { source },
});

export type ElementsActionFrameReadyMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.FrameReady;
};

export type ElementsActionPhaseMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Phase;
  requestId: string;
  payload: { phase: ElementsActionPhase };
};

export type ElementsActionResultMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Result;
  requestId: string;
  payload: { status: 'canceled' };
};

export type ElementsActionErrorMessage = {
  channel: typeof ELEMENTS_ACTION_CHANNEL;
  type: typeof ElementsActionMessageType.Error;
  requestId: string;
  payload: {
    code: string;
    message: string;
    recoverable: boolean;
  };
};

const ACTION_ENVELOPE_KEYS = ['channel', 'type', 'requestId', 'payload'] as const;
const FRAME_READY_MESSAGE_KEYS = ['channel', 'type'] as const;
const PHASE_PAYLOAD_KEYS = ['phase'] as const;
const RESULT_PAYLOAD_KEYS = ['status'] as const;
const ERROR_PAYLOAD_KEYS = ['code', 'message', 'recoverable'] as const;

const isEnvelope = (
  value: unknown,
  type: (typeof ElementsActionMessageType)[keyof typeof ElementsActionMessageType],
): value is Record<string, unknown> & { requestId: string; payload: unknown } =>
  isRecord(value) &&
  hasExactKeys(value, ACTION_ENVELOPE_KEYS) &&
  value.channel === ELEMENTS_ACTION_CHANNEL &&
  value.type === type &&
  isBoundedProtocolString(
    value.requestId,
    ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
  ) &&
  value.payload !== undefined;

export const isElementsActionFrameReadyMessage = (
  value: unknown,
): value is ElementsActionFrameReadyMessage =>
  isRecord(value) &&
  hasExactKeys(value, FRAME_READY_MESSAGE_KEYS) &&
  value.channel === ELEMENTS_ACTION_CHANNEL &&
  value.type === ElementsActionMessageType.FrameReady;

export const isElementsActionPhaseMessage = (value: unknown): value is ElementsActionPhaseMessage =>
  isEnvelope(value, ElementsActionMessageType.Phase) &&
  isRecord(value.payload) &&
  hasExactKeys(value.payload, PHASE_PAYLOAD_KEYS) &&
  typeof value.payload.phase === 'string' &&
  ['loading', 'content_ready', 'completing'].includes(value.payload.phase);

export const isElementsActionResultMessage = (
  value: unknown,
): value is ElementsActionResultMessage =>
  isEnvelope(value, ElementsActionMessageType.Result) &&
  isRecord(value.payload) &&
  hasExactKeys(value.payload, RESULT_PAYLOAD_KEYS) &&
  value.payload.status === 'canceled';

export const isElementsActionErrorMessage = (value: unknown): value is ElementsActionErrorMessage =>
  isEnvelope(value, ElementsActionMessageType.Error) &&
  isRecord(value.payload) &&
  hasExactKeys(value.payload, ERROR_PAYLOAD_KEYS) &&
  isBoundedProtocolString(
    value.payload.code,
    ELEMENTS_PROTOCOL_TEXT_LIMIT.opaqueIdentifierCharacters,
  ) &&
  isBoundedProtocolString(
    value.payload.message,
    ELEMENTS_PROTOCOL_TEXT_LIMIT.diagnosticMessageCharacters,
  ) &&
  typeof value.payload.recoverable === 'boolean';
